/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import { useAlert, useCompany } from '@orientaction/hooks';
import { getFromLS, valueAliasRole } from '@orientaction/utils';
import { EnvelopeSimpleIcon, FloppyDiskIcon } from '@phosphor-icons/react';
import { validate } from 'email-validator';
import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import { modifyUser } from '../../../../services/user.service';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { EMAIL_INVALID_MESSAGE, EMAIL_REGEX, EMAIL_TAKEN_MESSAGE } from '../../shared/constants';
import { ManagerOption } from '../../shared/types';
import {
  buildUpHierarchyPayload,
  mapManagerOptions,
  mapRoleOptions,
  needsAttachmentManager,
  readUpperHierarchy,
  toId,
  unwrapCollection,
} from '../../shared/utils';
import { PROFILE_UPDATED_MESSAGE } from '../constants';
import { useStyles } from '../styles';
import { ConsultantFormValues, userToConsultantForm } from '../utils';

const qs = require('qs');

interface ProfileEditFormProps {
  user: any;
  roleLabel: string;
  onSaved: (updated: any) => void;
}

const ProfileEditForm: FC<ProfileEditFormProps> = ({ user, roleLabel, onSaved }) => {
  const classes = useStyles();
  const alert = useAlert();
  const { companyUserRole, getCompanyUserRole, getUpHierachy, companies } = useCompany();
  const [form, setForm] = useState<ConsultantFormValues>(() => userToConsultantForm(user));
  const [emailError, setEmailError] = useState('');
  const [saving, setSaving] = useState(false);
  const [managers, setManagers] = useState<ManagerOption[]>([]);

  const token = getFromLS('userToken') || '';
  const userConnected = JSON.parse(getFromLS('userInfos') || '{}');
  const companyId = user?.company?.id || userConnected?.company?.id || 1;
  const isSuperAdmin = userConnected?.role?.type === 'admin';
  const connectedRoleId = Number(userConnected?.company_user_role?.id) || 0;
  const alias = companies.find((item: any) => Number(item.id) === Number(companyId));

  const roles = useMemo(
    () =>
      mapRoleOptions(companyUserRole, {
        isSuperAdmin,
        connectedRoleId,
        aliasName: name => valueAliasRole(name, alias?.attributes?.companyUserRoleAlias) || name,
      }),
    [companyUserRole, alias, isSuperAdmin, connectedRoleId]
  );

  const showManager = needsAttachmentManager(form.roleId);

  useEffect(() => {
    if (!companyUserRole?.length) {
      getCompanyUserRole().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    if (!form.roleId || !needsAttachmentManager(form.roleId)) {
      setManagers([]);
      return undefined;
    }
    let cancelled = false;
    const loadManagers = async () => {
      try {
        const response = await getUpHierachy(
          buildUpHierarchyPayload({
            companyId,
            roleId: form.roleId,
            isSuperAdmin,
            userConnected,
          })
        );
        if (cancelled) {
          return;
        }
        const list: ManagerOption[] = mapManagerOptions(response, user.id);
        const current = readUpperHierarchy(user?.upper_hierarchy);
        const currentId = toId(current);
        if (currentId && current && !list.some(item => item.id === currentId)) {
          list.unshift({
            id: currentId,
            firstname: current.firstname || '',
            lastname: current.lastname || '',
          });
        }
        setManagers(list);
      } catch {
        if (!cancelled) {
          setManagers([]);
        }
      }
    };
    loadManagers();
    return () => {
      cancelled = true;
    };
  }, [form.roleId, companyId, user.id]);

  const update = (patch: Partial<ConsultantFormValues>) => {
    setForm(prev => ({ ...prev, ...patch }));
    if (patch.email !== undefined) {
      setEmailError('');
    }
  };

  const emailIsTaken = async (email: string) => {
    const params = qs.stringify(
      {
        filters: { email: { $eq: email } },
        fields: ['id', 'email'],
      },
      { encodeValuesOnly: true }
    );
    const response = await apiServiceNative.get(`/api/users?${params}`);
    const found = unwrapCollection(response).find(
      (item: any) => Number(item.id) !== Number(user.id)
    );
    return Boolean(found);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!validate(email) || !EMAIL_REGEX.test(email)) {
      setEmailError(EMAIL_INVALID_MESSAGE);
      return;
    }
    if (!form.roleId) {
      return;
    }

    setSaving(true);
    try {
      if (email !== (user.email || '').trim().toLowerCase()) {
        if (await emailIsTaken(email)) {
          setEmailError(EMAIL_TAKEN_MESSAGE);
          return;
        }
      }

      const payload: any = {
        email,
        username: email,
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        company_user_role: Number(form.roleId),
        upper_hierarchy: showManager && form.managerId ? Number(form.managerId) : null,
      };

      const updated = await modifyUser({
        ...payload,
        id: user.id,
        token,
      });
      alert.setOpenAlert(true, PROFILE_UPDATED_MESSAGE, 'success');
      const selectedRole = roles.find(item => item.id === Number(form.roleId));
      const selectedManager = managers.find(item => item.id === Number(form.managerId));
      onSaved({
        ...(updated?.data || {}),
        ...payload,
        company_user_role: selectedRole || { id: Number(form.roleId), name: roleLabel },
        upper_hierarchy: selectedManager || null,
      });
    } catch (error: any) {
      const message = String(
        error?.response?.data?.error?.message || error?.message || error || ''
      );
      if (message.includes('email.taken') || message.includes('already taken')) {
        setEmailError(EMAIL_TAKEN_MESSAGE);
        return;
      }
      alert.openSimpleAlert();
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    Boolean(form.roleId) &&
    Boolean(form.firstname.trim()) &&
    Boolean(form.lastname.trim()) &&
    Boolean(form.email.trim()) &&
    (!showManager || Boolean(form.managerId));

  return (
    <form className={classes.editForm} onSubmit={handleSubmit}>
      <div className={classes.editFormBody}>
      <h2 className={classes.sectionTitle}>Informations</h2>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="consultant-profile-role">
          Rôle
        </label>
        <FormControl variant="outlined" className={classes.textField}>
          <Select
            id="consultant-profile-role"
            value={form.roleId}
            displayEmpty={true}
            onChange={event => {
              const value = event.target.value as string | number;
              update({ roleId: value === '' ? '' : Number(value), managerId: '' });
            }}>
            <MenuItem value="">
              <span className={classes.placeholder}>Sélectionner un rôle</span>
            </MenuItem>
            {roles.map(role => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="consultant-profile-firstname">
          Prénom
        </label>
        <TextField
          id="consultant-profile-firstname"
          className={classes.textField}
          variant="outlined"
          value={form.firstname}
          onChange={event => update({ firstname: event.target.value })}
        />
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="consultant-profile-lastname">
          Nom
        </label>
        <TextField
          id="consultant-profile-lastname"
          className={classes.textField}
          variant="outlined"
          value={form.lastname}
          onChange={event => update({ lastname: event.target.value })}
        />
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="consultant-profile-email">
          Mail
        </label>
        <TextField
          id="consultant-profile-email"
          className={classes.textField}
          variant="outlined"
          type="email"
          value={form.email}
          error={Boolean(emailError)}
          onChange={event => update({ email: event.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <EnvelopeSimpleIcon size={20} color="#8B90A5" />
              </InputAdornment>
            ),
          }}
        />
        {emailError ? <div className={classes.fieldError}>{emailError}</div> : null}
      </div>

      {showManager && (
        <div className={classes.field}>
          <label className={classes.label} htmlFor="consultant-profile-manager">
            Administrateur de rattachement
          </label>
          <FormControl variant="outlined" className={classes.textField}>
            <Select
              id="consultant-profile-manager"
              value={form.managerId}
              displayEmpty={true}
              onChange={event => {
                const value = event.target.value as string | number;
                update({ managerId: value === '' ? '' : Number(value) });
              }}>
              <MenuItem value="">
                <span className={classes.placeholder}>Sélectionner un responsable</span>
              </MenuItem>
              {managers.map(manager => (
                <MenuItem key={manager.id} value={manager.id}>
                  {`${manager.firstname || ''} ${manager.lastname || ''}`.trim()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}
      </div>

      <div className={classes.formFooter}>
        <Button
          type="submit"
          className={classes.saveBtn}
          startIcon={<FloppyDiskIcon size={18} />}
          disabled={saving || !canSave}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </form>
  );
};

export default ProfileEditForm;
